import { MutableRefObject, useEffect, useMemo, useRef, useState } from "react";
import timestampService from "../../service/timestampService";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import labels from "../../utils/labels";
import TimePicker from "../UI/input/TimePicker";
import LabeledSection from "../UI/LabeledSection";
import EmployeeSettingsService from "../../service/employeeSettingsService";
import {
  clearEmployeeTransportEdit,
  clearEmployeeWorkTimeEdit,
  editEmployeeTransportType,
  editEmployeeWorkTime,
} from "../../store/employeeSettings";
import { updateEmployeeWorkTime } from "../../store/ganttChart";
import { getGanttChartData } from "../../store/ganttChart/action";
import StandardButton from "../UI/buttons/StandardButton";
import EmployeeSettingsStyles from "../../styles/employeeSettings.module.css";
import { useQueryParams } from "../../hooks/useQueryParams";
import {
  EmployeeTransport,
  EmployeeWorkTime,
  TransportationType,
} from "../../types/ganttChart";
import useDataSelector from "../../hooks/useDataSelector";

type LocalEmployeeWorkTime = {
  workTimeStart: string;
  workTimeEnd: string;
};
const localDefaultTime: LocalEmployeeWorkTime = {
  workTimeStart: "",
  workTimeEnd: "",
};

const timeToMinutes = (time: string) => {
  if (!time) return Number.NaN;
  const [hours, minutes] = time.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return Number.NaN;
  return hours * 60 + minutes;
};

const EmployeeWorktime = ({
  onUnsavedEdit,
  onDeleteWorkTimeRequest,
  saveRef,
  hasWorkTime,
  onPendingChange,
}: {
  onUnsavedEdit: (hasChanges: boolean) => void;
  onDeleteWorkTimeRequest: () => void;
  saveRef?: MutableRefObject<(() => Promise<void>) | null>;
  hasWorkTime: boolean;
  onPendingChange?: (isPending: boolean) => void;
}) => {
  const { employeeShiftId } = useAppSelector(
    (state) => state.drawerSlice.selectedEmployee!,
  );
  const { manualEdits } = useAppSelector(
    (state) => state.employeeSettingsSlice,
  );
  const { dataToUse } = useDataSelector();
  const employee = dataToUse?.employees.find(
    (e) => e.shiftSchedule.idShift === employeeShiftId,
  );
  const employeeWorkTime = useMemo<EmployeeWorkTime>(
    () => ({
      workTimeStart: employee?.shiftStartTime ?? null,
      workTimeEnd: employee?.shiftEndTime ?? null,
    }),
    [employee?.shiftStartTime, employee?.shiftEndTime],
  );
  const manualShiftChanges = employee?.manualShiftChanges;
  const dispatch = useAppDispatch();
  const [localWorkTime, setLocalWorkTime] =
    useState<LocalEmployeeWorkTime>(localDefaultTime);
  const initialWorkTimeRef = useRef<LocalEmployeeWorkTime>(localDefaultTime);
  const [isPending, setIsPending] = useState(false);
  const queryKey = useQueryParams();
  const { date } = queryKey;

  const manualWorkTimeEdits =
    manualEdits[employeeShiftId]?.employeeWorkTime ??
    manualShiftChanges?.employeeWorkTime;
  const worktimeToDisplay = manualWorkTimeEdits ?? employeeWorkTime;

  const startEqualsEnd =
    localWorkTime.workTimeStart !== "" &&
    localWorkTime.workTimeEnd !== "" &&
    localWorkTime.workTimeStart === localWorkTime.workTimeEnd;

  const hasCurrentWorktime =
    //manualWorkTimeEdits accept undefined for start and end time, so we need loose inequality.
    manualWorkTimeEdits?.workTimeStart != null ||
    manualWorkTimeEdits?.workTimeEnd != null ||
    hasWorkTime;

  useEffect(() => {
    if (isPending) return;
    if (!hasCurrentWorktime) {
      setLocalWorkTime(localDefaultTime);
      initialWorkTimeRef.current = localDefaultTime;
      return;
    }

    const { workTimeStart, workTimeEnd } = worktimeToDisplay;
    if (!workTimeStart || !workTimeEnd) return;

    const mappedTime = {
      workTimeStart:
        timestampService.getHoursAndMinutesTimestamp(workTimeStart),
      workTimeEnd: timestampService.getHoursAndMinutesTimestamp(workTimeEnd),
    };

    setLocalWorkTime(mappedTime);
    initialWorkTimeRef.current = mappedTime;
  }, [employeeShiftId, worktimeToDisplay, hasCurrentWorktime, isPending]);

  function onInputChange(time: "start" | "end", value: string) {
    const newTimeWindow = {
      workTimeStart: time === "start" ? value : localWorkTime.workTimeStart,
      workTimeEnd: time === "end" ? value : localWorkTime.workTimeEnd,
    };
    setLocalWorkTime(newTimeWindow);
  }

  const hasChanges =
    localWorkTime.workTimeStart !== "" &&
    localWorkTime.workTimeEnd !== "" &&
    (localWorkTime.workTimeStart !== initialWorkTimeRef.current.workTimeStart ||
      localWorkTime.workTimeEnd !== initialWorkTimeRef.current.workTimeEnd);

  useEffect(() => {
    onUnsavedEdit(hasChanges);
  }, [onUnsavedEdit, hasChanges]);

  async function handleTimeWindowUpdate() {
    const baseDateForMerge = !hasCurrentWorktime
      ? date
      : worktimeToDisplay.workTimeStart;
    if (!baseDateForMerge) return;
    const updatedStart = timestampService.mergeTimeIntoDate(
      baseDateForMerge,
      localWorkTime.workTimeStart,
    );
    const updatedEnd = timestampService.mergeTimeIntoDate(
      baseDateForMerge,
      localWorkTime.workTimeEnd,
    );
    const matchesBaseWorkTime =
      updatedStart === employeeWorkTime.workTimeStart &&
      updatedEnd === employeeWorkTime.workTimeEnd;
    setIsPending(true);
    onPendingChange?.(true);

    try {
      await EmployeeSettingsService.editEmployeeWorkTime(
        employeeShiftId,
        updatedStart,
        updatedEnd,
        dispatch,
      );
      dispatch(
        editEmployeeWorkTime({
          shiftId: employeeShiftId,
          workTimeStart: updatedStart,
          workTimeEnd: updatedEnd,
        }),
      );
      dispatch(
        updateEmployeeWorkTime({
          shiftId: employeeShiftId,
          shiftStartTime: updatedStart,
          shiftEndTime: updatedEnd,
        }),
      );
      // if the edit is back to original worktime - clear that from the store
      if (matchesBaseWorkTime)
        dispatch(clearEmployeeWorkTimeEdit({ shiftId: employeeShiftId }));
      if (!hasCurrentWorktime) {
        const defaultTransportType: TransportationType = "CAR";
        const defaultSegments: EmployeeTransport[] = [
          {
            transportationType: defaultTransportType,
            start: updatedStart,
            end: updatedEnd,
          },
        ];
        await EmployeeSettingsService.editTransportSegments(
          employeeShiftId,
          defaultSegments,
          dispatch,
        );
        dispatch(
          editEmployeeTransportType({
            shiftId: employeeShiftId,
            transportationType: defaultSegments,
          }),
        );
        dispatch(clearEmployeeTransportEdit({ shiftId: employeeShiftId }));
      }

      // Arbejdstid changes affect the backend's KPI recompute (Medarbejder
      // timer/aftaler) regardless of whether this is a first-time assignment
      // or an edit of an existing worktime, so always refresh the visible Gantt.
      if (
        queryKey.filterId != null &&
        queryKey.date != null &&
        queryKey.solutionType != null
      ) {
        await dispatch(
          getGanttChartData({
            filterId: queryKey.filterId,
            date: queryKey.date,
            solutionType: queryKey.solutionType,
          }),
        );
      }
      initialWorkTimeRef.current = localWorkTime;
    } catch {
      setLocalWorkTime(initialWorkTimeRef.current);
    }
    setIsPending(false);
    onPendingChange?.(false);
  }

  const isDraftIncomplete =
    localWorkTime.workTimeStart === "" || localWorkTime.workTimeEnd === "";

  const workTimeStartInMinutes = timeToMinutes(localWorkTime.workTimeStart);
  const workTimeEndInMinutes = timeToMinutes(localWorkTime.workTimeEnd);
  const invalidTimeWindow = workTimeEndInMinutes <= workTimeStartInMinutes;
  const hasNonParsableTimes =
    Number.isNaN(workTimeStartInMinutes) || Number.isNaN(workTimeEndInMinutes);

  const canSave =
    hasChanges &&
    !isDraftIncomplete &&
    !invalidTimeWindow &&
    !hasNonParsableTimes &&
    !startEqualsEnd;
  if (saveRef) saveRef.current = canSave ? handleTimeWindowUpdate : null;

  return (
    <LabeledSection
      title={labels.editWorkingHours}
      titleSize="h4"
      spacing="small"
      extraOptions={
        hasCurrentWorktime && (
          <StandardButton
            ariaLabel={labels.deleteWorkTime}
            variant="TERTIARY"
            onClick={onDeleteWorkTimeRequest}
            classes="!h-[17.5px]"
          >
            {labels.deleteWorkTime}
          </StandardButton>
        )
      }
    >
      <div className="flex flex-col w-full gap-[var(--spacing-md)]">
        <div
          className={`flex gap-[var(--spacing-md)]
          ${manualWorkTimeEdits && EmployeeSettingsStyles.editedSetting}`}
        >
          <TimePicker
            id="employee-start-time"
            value={localWorkTime.workTimeStart}
            onTimeChange={(newStart) => {
              onInputChange("start", newStart);
            }}
            label={labels.startTime}
            classes="w-full"
            helperText={manualWorkTimeEdits && labels.manuallyEditedWorkTime}
          />
          <TimePicker
            id="employee-end-time"
            value={localWorkTime.workTimeEnd}
            onTimeChange={(newEnd) => {
              onInputChange("end", newEnd);
            }}
            label={labels.endTime}
            classes="w-full"
            helperText={manualWorkTimeEdits && labels.manuallyEditedWorkTime}
          />
        </div>
        {startEqualsEnd && (
          <p className="text-red-500 text-sm">
            {labels.workTimeStartEqualsEndError}
          </p>
        )}
        {!startEqualsEnd &&
          invalidTimeWindow &&
          !hasNonParsableTimes &&
          !isDraftIncomplete && (
            <p className="text-red-500 text-sm">
              {labels.workTimeEndBeforeStartError}
            </p>
          )}
        {(hasChanges || isPending) && (
          <div className="flex gap-[var(--spacing-sm)] w-full justify-end">
            <StandardButton
              ariaLabel={labels.save}
              variant="PRIMARY"
              classes="w-[84px]"
              onClick={handleTimeWindowUpdate}
              pending={isPending}
              disable={
                isDraftIncomplete ||
                invalidTimeWindow ||
                hasNonParsableTimes ||
                startEqualsEnd
              }
            >
              {labels.save}
            </StandardButton>
          </div>
        )}
        {/* {transportConflictsWithWorkTime && (
          <p className="text-[var(--col-error-4)] text-sm">
            {labels.errorWorkTimeConflictsWithTransport}
          </p>
        )} */}
      </div>
    </LabeledSection>
  );
};

export default EmployeeWorktime;
